/**
 * Zoho CRM Sync Manager
 * Admin page to view lead sync status and retry failed syncs
 */
import { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Search,
  Link2,
  Zap,
  FileUp,
  AlertTriangle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';

const API_URL = window.location.origin;

const ZohoSyncManager = () => {
  const { isDark } = useAdminTheme();
  const { token, logout } = useCMSAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ pending: 0, synced: 0, error: 0 });
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [authorizing, setAuthorizing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, synced, error
  const [linkDialog, setLinkDialog] = useState({ open: false, lead: null });
  const [zohoLeadId, setZohoLeadId] = useState('');

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  // Fetch leads and stats
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Zoho status
      const statusRes = await fetch(`${API_URL}/api/zoho/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(statusRes)) return;
      const statusData = await statusRes.json();
      setStats(statusData.leads_stats || { pending: 0, synced: 0, error: 0 });
      setIntegrationStatus(statusData);

      // Fetch leads from leads collection
      const leadsRes = await fetch(`${API_URL}/api/leads/admin/all?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(leadsRes)) return;
      const leadsData = await leadsRes.json();
      setLeads(leadsData.leads || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Sync all pending leads
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/zoho/sync-pending`, { method: 'POST' });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Synced ${data.results.synced} of ${data.results.total} leads`);
        if (data.results.failed > 0) {
          toast.warning(`${data.results.failed} leads failed to sync`);
        }
        fetchData();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Error syncing leads');
    } finally {
      setSyncing(false);
    }
  };

  // Retry single lead sync
  const handleRetrySync = async (leadId) => {
    try {
      // First update the lead status to pending
      await fetch(`${API_URL}/api/leads/admin/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zoho_sync_status: 'pending' })
      });
      
      // Then trigger sync
      const res = await fetch(`${API_URL}/api/zoho/sync-pending`, { method: 'POST' });
      const data = await res.json();
      
      if (data.results?.synced > 0) {
        toast.success('Lead synced successfully');
      } else {
        toast.error('Sync failed: ' + (data.results?.errors?.[0]?.error || 'Unknown error'));
      }
      fetchData();
    } catch (error) {
      toast.error('Error retrying sync');
    }
  };

  // Auto-link a single lead by searching Zoho via email
  const [autoLinking, setAutoLinking] = useState(null); // leadId being linked or 'all'
  const [resyncingDocs, setResyncingDocs] = useState(null); // leadId currently re-syncing docs
  const handleAutoLinkOne = async (leadId) => {
    setAutoLinking(leadId);
    try {
      const res = await fetch(`${API_URL}/api/leads/admin/${leadId}/auto-link-zoho`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.found_in_zoho) {
        toast.success(`Linked to Zoho Lead ${data.zoho_lead_id}`);
      } else {
        toast.error(data.message || 'No matching lead found in Zoho');
      }
      fetchData();
    } catch (error) {
      toast.error('Auto-link failed');
    } finally {
      setAutoLinking(null);
    }
  };

  // Re-push student documents to the linked Zoho Lead (Notes)
  const handleResyncDocs = async (leadId) => {
    setResyncingDocs(leadId);
    try {
      const res = await fetch(`${API_URL}/api/leads/admin/${leadId}/resync-docs-to-zoho`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Re-synced ${data.documents_synced} document(s) to Zoho`);
      } else {
        toast.error(data.error || data.message || data.detail || 'Re-sync failed');
      }
    } catch (error) {
      toast.error('Re-sync request failed');
    } finally {
      setResyncingDocs(null);
    }
  };

  // Bulk auto-link all errored leads by email lookup
  const handleAutoLinkAllErrors = async () => {    if (!window.confirm('Search Zoho CRM by email for every errored lead and auto-link any matches. Continue?')) {
      return;
    }
    setAutoLinking('all');
    try {
      const res = await fetch(`${API_URL}/api/leads/admin/auto-link-all-errors`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        const s = data.summary || {};
        toast.success(`Checked ${s.checked || 0}: ${s.linked || 0} linked, ${s.not_found || 0} not in Zoho, ${s.failed || 0} failed`);
      } else {
        toast.error('Bulk auto-link failed');
      }
      fetchData();
    } catch (error) {
      toast.error('Bulk auto-link failed');
    } finally {
      setAutoLinking(null);
    }
  };

  // Manual link to Zoho
  const handleManualLink = async () => {    if (!zohoLeadId.trim()) {
      toast.error('Please enter a Zoho Lead ID');
      return;
    }

    try {
      // Update lead with Zoho ID
      const res = await fetch(`${API_URL}/api/leads/admin/${linkDialog.lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          zoho_lead_id: zohoLeadId.trim(),
          zoho_sync_status: 'synced',
          zoho_sync_error: null
        })
      });

      if (res.ok) {
        toast.success('Lead linked to Zoho successfully');
        setLinkDialog({ open: false, lead: null });
        setZohoLeadId('');
        fetchData();
      } else {
        toast.error('Failed to link lead');
      }
    } catch (error) {
      toast.error('Error linking lead');
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filter === 'all' ||
      lead.zoho_sync_status === filter;

    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'synced':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle2 className="w-3 h-3 mr-1" /> Synced</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'error':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      default:
        return <Badge variant="outline"><AlertTriangle className="w-3 h-3 mr-1" /> Unknown</Badge>;
    }
  };

  const handleReAuthorize = async () => {
    setAuthorizing(true);
    try {
      // Pass current origin as redirect_uri so it works in preview AND staging
      // (must be whitelisted in Zoho API Console).
      const redirectUri = `${window.location.origin}/api/zoho/callback`;
      const res = await fetch(
        `${API_URL}/api/zoho/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (handleUnauthorized(res)) return;
      const data = await res.json();
      if (!data.authorization_url) {
        toast.error('Failed to get Zoho authorization URL');
        return;
      }
      toast.info('Opening Zoho consent window…');
      // Open Zoho consent in a new tab; after redirect to /api/zoho/callback,
      // the backend renders a small success page and saves tokens.
      const popup = window.open(data.authorization_url, '_blank', 'noopener,noreferrer');
      if (!popup) toast.error('Popup blocked. Please allow popups and try again.');

      // Poll status every 3s for up to 2 minutes
      let elapsed = 0;
      const poll = setInterval(async () => {
        elapsed += 3;
        await fetchData();
        if (integrationStatus?.has_refresh_token || elapsed >= 120) {
          clearInterval(poll);
          if (integrationStatus?.has_refresh_token) toast.success('Zoho re-authorized successfully!');
        }
      }, 3000);
    } catch (e) {
      console.error(e);
      toast.error('Failed to start Zoho re-authorization');
    } finally {
      setAuthorizing(false);
    }
  };

  const handleClearTokens = async () => {
    if (!window.confirm('Clear stored Zoho tokens? You will need to re-authorize.')) return;
    try {
      const res = await fetch(`${API_URL}/api/zoho/clear-tokens`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        toast.success('Tokens cleared');
        fetchData();
      } else {
        toast.error('Failed to clear tokens');
      }
    } catch (e) {
      toast.error('Failed to clear tokens');
    }
  };

  return (
    <AdminLayout>
      <div className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Zoho CRM Sync Manager
            </h1>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Monitor and manage lead synchronization with Zoho CRM
            </p>
          </div>
          <div className="flex items-center gap-2">
            {stats.error > 0 && (
              <Button
                onClick={handleAutoLinkAllErrors}
                disabled={autoLinking === 'all'}
                variant="outline"
                className="gap-2"
                data-testid="auto-link-all-errors-btn"
                title="Search Zoho by email for every errored lead and auto-link matches"
              >
                {autoLinking === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Auto-link All Errors ({stats.error})
              </Button>
            )}
            <Button 
              onClick={handleSyncAll} 
              disabled={syncing || stats.pending === 0}
              className="gap-2"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync All Pending ({stats.pending})
            </Button>
          </div>
        </div>

        {/* Integration Status Banner */}
        {integrationStatus && (
          <Card
            className={`${
              integrationStatus.has_refresh_token
                ? (isDark ? 'bg-emerald-900/30 border-emerald-700/40' : 'bg-emerald-50 border-emerald-200')
                : (isDark ? 'bg-amber-900/30 border-amber-700/40' : 'bg-amber-50 border-amber-200')
            }`}
            data-testid="zoho-integration-status"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 flex-1 min-w-[280px]">
                  <div className={`p-2 rounded-lg ${integrationStatus.has_refresh_token ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                    {integrationStatus.has_refresh_token
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {integrationStatus.has_refresh_token ? 'Zoho CRM is connected' : 'Zoho CRM is NOT authorized'}
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {integrationStatus.has_refresh_token
                        ? `Refresh token saved. Access token ${integrationStatus.access_token_valid ? 'valid' : 'expired (auto-refreshes on next sync)'}.`
                        : 'No refresh token in DB. Sync operations will silently no-op until you re-authorize.'}
                    </p>
                    <p className={`text-[11px] mt-2 font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                      Redirect URI: {integrationStatus.redirect_uri || '(not set)'}
                    </p>
                    {(integrationStatus.sync_log?.total || 0) > 0 && (
                      <p className={`text-[11px] mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {integrationStatus.sync_log.total} sync log entries.
                        {integrationStatus.sync_log.recent?.[0] && (
                          <> Last: {integrationStatus.sync_log.recent[0].sync_type} → {integrationStatus.sync_log.recent[0].status} ({new Date(integrationStatus.sync_log.recent[0].timestamp).toLocaleString()})</>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleReAuthorize}
                    disabled={authorizing}
                    className="gap-2"
                    data-testid="zoho-reauthorize-btn"
                  >
                    {authorizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    {integrationStatus.has_refresh_token ? 'Re-Authorize' : 'Authorize Zoho'}
                  </Button>
                  {integrationStatus.has_refresh_token && (
                    <Button
                      onClick={handleClearTokens}
                      variant="outline"
                      className="gap-2"
                      data-testid="zoho-clear-tokens-btn"
                    >
                      <XCircle className="w-4 h-4" />
                      Clear Tokens
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-500/20">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>{stats.synced}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Synced to Zoho</p>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-yellow-500/20">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>{stats.pending}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Pending Sync</p>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-red-500/20">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : ''}`}>{stats.error}</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sync Errors</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-slate-800 border-slate-700' : ''}`}
            />
          </div>
          
          <div className="flex gap-2">
            {['all', 'pending', 'synced', 'error'].map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className="capitalize"
              >
                {f}
              </Button>
            ))}
          </div>
        </div>

        {/* Leads Table */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Zoho ID</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No leads found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeads.map((lead) => (
                      <TableRow key={lead.id} className={isDark ? 'border-slate-700' : ''}>
                        <TableCell className="font-medium">
                          {lead.first_name} {lead.last_name}
                        </TableCell>
                        <TableCell className="text-sm">{lead.email}</TableCell>
                        <TableCell className="text-sm">{lead.phone}</TableCell>
                        <TableCell className="text-sm">{lead.preferred_branch}</TableCell>
                        <TableCell>{getStatusBadge(lead.zoho_sync_status)}</TableCell>
                        <TableCell className="text-sm font-mono">
                          {lead.zoho_lead_id ? (
                            <a 
                              href={`https://crm.zoho.com/crm/org20098319797/tab/Leads/${lead.zoho_lead_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:underline flex items-center gap-1"
                            >
                              {lead.zoho_lead_id.slice(-8)}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-red-400 max-w-[150px] truncate" title={lead.zoho_sync_error}>
                          {lead.zoho_sync_error || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {lead.zoho_sync_status === 'error' && !lead.zoho_lead_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAutoLinkOne(lead.id)}
                                disabled={autoLinking === lead.id}
                                title="Auto-link by Email (search Zoho)"
                                data-testid={`auto-link-btn-${lead.id}`}
                              >
                                {autoLinking === lead.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Zap className="w-3 h-3" />}
                              </Button>
                            )}
                            {lead.zoho_sync_status !== 'synced' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRetrySync(lead.id)}
                                title="Retry Sync"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </Button>
                            )}
                            {lead.zoho_sync_status === 'synced' && lead.zoho_lead_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResyncDocs(lead.id)}
                                disabled={resyncingDocs === lead.id}
                                title="Re-sync student documents to Zoho (Notes)"
                                data-testid={`resync-docs-btn-${lead.id}`}
                              >
                                {resyncingDocs === lead.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <FileUp className="w-3 h-3" />}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setLinkDialog({ open: true, lead });
                                setZohoLeadId(lead.zoho_lead_id || '');
                              }}
                              title="Manual Link"
                            >
                              <Link2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Manual Link Dialog */}
        <Dialog open={linkDialog.open} onOpenChange={(open) => setLinkDialog({ open, lead: linkDialog.lead })}>
          <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
            <DialogHeader>
              <DialogTitle>Link Lead to Zoho CRM</DialogTitle>
              <DialogDescription>
                Manually link this lead to an existing Zoho CRM record.
                <br />
                Lead: <strong>{linkDialog.lead?.first_name} {linkDialog.lead?.last_name}</strong> ({linkDialog.lead?.email})
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-slate-300' : ''}`}>
                  Zoho Lead ID
                </label>
                <Input
                  placeholder="e.g., 4736456000405310019"
                  value={zohoLeadId}
                  onChange={(e) => setZohoLeadId(e.target.value)}
                  className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Find this in the Zoho CRM lead URL: /Leads/<strong>4736456000XXXXXXXXX</strong>
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkDialog({ open: false, lead: null })}>
                Cancel
              </Button>
              <Button onClick={handleManualLink}>
                <Link2 className="w-4 h-4 mr-2" />
                Link Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </AdminLayout>
  );
};

export default ZohoSyncManager;
